import { useEffect, useId, useRef } from "react";
import { formatText } from "../i18n/texts";
import { ChoiceMenu } from "../ui/ChoiceMenu";
import dataTableStyles from "../ui/DataTableFrame.module.css";
import { ChatIcon } from "../ui/icons/Icon";
import { pagerMode } from "../ui/pagination";
import { ChatAvatar } from "./ChatAvatar";
import { ContactTagBadge } from "./ContactTagBadge";
import {
  channel,
  fullName,
  phone,
  sortColumn,
  sortDescending,
  sortField,
  type ContactPageResult,
  type ContactRow,
  type ContactSession,
} from "./contactModel";
import type { ContactWorkspaceController, WorkspaceView } from "./contactWorkspaceController";
import { Highlighted } from "./searchText";
import { count } from "./tagFormat";
import * as tagFit from "./tagListFit";

// Tabla de Contactos (ContactTable.razor, ContactTagList.razor y ContactPagination.razor del portal actual): mismo HTML.
// Estos componentes no tienen CSS propio en el portal actual: sus elementos no llevan clase de alcance.

type Props = { ctl: ContactWorkspaceController; view: WorkspaceView };

const opensThread = (thread: ContactSession | null): thread is ContactSession =>
  !!thread && (thread.conversationId !== null || thread.sessionId !== null);

/** C15 1.14.4 (D11): primero las etiquetas del filtro aplicado, en el orden en que se eligieron; después las demás
 * por nombre (sin distinguir mayúsculas ni tildes). Sin filtro de etiquetas, todas por nombre. */
export function orderTags(tags: string[], filter: readonly string[]): string[] {
  const same = (a: string, b: string) => a.localeCompare(b, "es", { sensitivity: "base" }) === 0;
  const first: string[] = [];
  for (const chosen of filter) {
    const tag = tags.find((item) => same(item, chosen));
    if (tag !== undefined && !first.includes(tag)) first.push(tag);
  }
  const rest = tags.filter((tag) => !first.includes(tag)).sort((a, b) => a.localeCompare(b, "es", { sensitivity: "base" }));
  return [...first, ...rest];
}

/** Etiquetas de una fila, en una sola línea: las que no entran pasan a «+N» (tagListFit), con «También: …». */
export function ContactTagList({
  ctl,
  tags: unordered,
  name,
  filter = [],
}: {
  ctl: ContactWorkspaceController;
  tags: string[];
  name: string;
  filter?: readonly string[];
}) {
  const tags = orderTags(unordered, filter);
  const popoverId = "c2-tags-" + useId().replace(/[^a-z0-9]/gi, "");
  if (tags.length === 0) return null;
  return (
    <>
      <span className="c2-tags" data-tags-also={ctl.L("R2_TAGS_ALSO")}>
        {tags.map((tag) => (
          <span
            key={tag}
            className="chip"
            title={tag}
            data-shell-tooltip={tag}
            tabIndex={0}
            aria-label={tag}
            data-tag-chip=""
          >
            <ContactTagBadge name={tag} color={ctl.tagColor(tag)} />
          </span>
        ))}
        {/* Oculto desde el render: el ajuste solo lo muestra si hay exceso. */}
        <button
          className="button ghost c1-icon c2-tag-trigger"
          hidden
          onClick={(event) => event.stopPropagation()}
          data-tag-more=""
          popoverTarget={popoverId}
          aria-controls={popoverId}
          aria-label={formatText(ctl.L2("TAGS_MORE"), name)}
        >
          <span data-tag-count=""></span>
        </button>
      </span>
      <div
        id={popoverId}
        popover="auto"
        data-contact-popover=""
        className="c2-tag-popover c1-sheet"
        aria-label={ctl.L("TAGS")}
      >
        <div className="c1-sheet-head">
          <button
            type="button"
            className="c1-icon icon-button"
            popoverTarget={popoverId}
            popoverTargetAction="hide"
            aria-label={ctl.L("BACK")}
          >
            <ChatIcon name="arrow-left" />
          </button>
          <h2>{ctl.L("TAGS")}</h2>
        </div>
        <ul>
          {tags.map((tag) => (
            <li key={tag}>
              <ContactTagBadge name={tag} color={ctl.tagColor(tag)} />
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}

export function ContactTable({ ctl, view }: Props) {
  // I13/C22 · en la primera carga el encabezado se arma igual; las filas llegan con la lista.
  const page = view.page;
  const items = page?.items ?? [];
  const canChat = page?.access.canOpenChat ?? view.contextRights?.canOpenChat ?? true;
  const tableRoot = useRef<HTMLTableElement>(null);
  const listRoot = useRef<HTMLDivElement>(null);
  const directory = ctl.showDirectory;
  // Un solo ajuste para todas las filas, después de cada dibujo (50 llamadas por página goteaban 120 ms).
  useEffect(() => {
    if (directory) tagFit.fitAll(tableRoot.current, listRoot.current);
    else tagFit.fitAll(tableRoot.current);
  });
  useEffect(() => () => tagFit.disposeAll(), []);
  // K4 · C15/C28 1.14.5: con filtro de etiquetas, la columna Etiquetas pide el ancho de las del filtro (hasta 2, enteras),
  // 6 px entre ellas y «+99»: una copia oculta con la misma etiqueta va en su encabezado (.c2-tag-probe-list) y el motor
  // de columnas la mide después del layout, como el resto de los anchos (medirla aquí forzaba el layout y redibujaba la
  // tabla al entrar a Contactos). Solo depende del filtro, nunca de las filas.
  const filterTags = (view.query.tags ?? []).slice(0, 2);
  const sort = view.query.sort;
  const direction = (field: string) =>
    sortColumn(sort) === sortField(field) ? (sortDescending(sort) ? "descending" : "ascending") : "none";
  /** El orden en palabras en la flecha del título («Ordenado: más reciente», «Ordenado: A–Z»). */
  const sortedTitle = (field: string) => {
    const order = ctl.orderLabel(sortField(field), direction(field) === "descending");
    const lower = order.length > 1 && order[1] !== "–" ? order[0]!.toLowerCase() + order.slice(1) : order;
    return formatText(ctl.L("SORTED"), lower);
  };
  const header = (field: string) =>
    field === "Tags" ? (
      <span className="c1-column-static">{ctl.field(field)}</span>
    ) : (
      <button type="button" className="c1-column-order" onClick={() => void ctl.sortColumn(field)}>
        <span>{ctl.field(field)}</span>
        {direction(field) !== "none" ? (
          <span className="c1-order-arrow" title={sortedTitle(field)}>
            <ChatIcon name="arrow-down" className={direction(field) === "ascending" ? "c1-order-up" : undefined} />
          </span>
        ) : null}
      </button>
    );
  const value = (row: ContactRow, field: string) => {
    const person = row.contact,
      session = row.conversation;
    switch (field) {
      case "Phone":
        return phone(person.phone);
      case "Email":
        return person.email ?? "—";
      case "CreatedAt":
        return ctl.date(person.createdAt);
      case "Agent":
        return session?.sessionId === null || session?.sessionId === undefined
          ? "—"
          : (session.agent ?? ctl.L(session.agentEmployeeId !== null ? "AI_AGENT" : "UNASSIGNED"));
      case "Inbox":
        return session?.inbox ?? "—";
      case "RelationshipStatus":
        return person.relationshipStatus === null ? ctl.L("NONE") : ctl.L(person.relationshipStatus);
      case "Channel":
        return channel(session);
      default:
        return "—";
    }
  };
  const columnClass = (field: string) => "c1-col-" + field.toLowerCase();
  /** K1 · C22/C28 1.14.5: el dato que la columna debe mostrar entero, sin mirar las filas (el motor de columnas mide
   * el título y esto). Lead: lo que va antes del texto (el avatar del agente, 24 px; el agente es texto libre). */
  const longest = (...texts: string[]) => texts.reduce((a, b) => (b.length > a.length ? b : a), "");
  const measure = (field: string): Record<string, string> => {
    switch (field) {
      case "Phone":
        return { "data-column-sample": "+51 999 999 999" };
      case "CreatedAt":
        return { "data-column-sample": ctl.date("2000-12-28T22:58:00Z") };
      case "Agent":
        return { "data-column-lead": "24" };
      case "RelationshipStatus":
        return { "data-column-sample": longest(...["NONE", "PROSPECT", "CUSTOMER", "PARTNER", "OTHER"].map(ctl.L)) };
      case "Channel":
        return { "data-column-sample": "WhatsApp · +51 999 999 999" };
      default:
        return {};
    }
  };
  const selected = view.selected;
  const allSelected = items.length > 0 && items.every((item) => selected.has(item.contact.id));
  const anySelected = items.some((item) => selected.has(item.contact.id));
  const fields = ["Phone", "Tags", "CreatedAt", "Agent", "Inbox", "Email", "RelationshipStatus", "Channel"];
  const base = ctl.baseFields(page).join(",");
  const defaults = ctl.visibleFields(view).join(",");
  const term = view.query.search;
  const open = (row: ContactRow) => (view.edit === null && view.open === row.contact.id ? "true" : undefined);
  return (
    <>
      <div
        className={`bh-table-frame c2-directory-table c1-table-scroll ${view.autoColumns ? "c1-auto" : "c1-chosen"} ${dataTableStyles.scope}`}
        tabIndex={0}
        role="region"
        aria-label={ctl.L("TITLE")}
      >
        <table
          ref={tableRoot}
          className="c1-table c2-contact-table"
          data-report-id="chat.contacts"
          data-default-columns={defaults}
          data-base-columns={base}
        >
          {/* J4 · C22 1.14.4: sin la vista resuelta (primera visita, sin página ni bandejas conocidas), el encabezado
              espera: nunca unas columnas que después cambian. */}
          <thead style={page === null && !ctl.viewKnown() ? { visibility: "hidden" } : undefined}>
            <tr>
              <th
                className="c1-check"
                data-column-id="selection"
                data-column-label={ctl.L("SELECTION_COLUMN")}
                data-column-required="true"
              >
                <input
                  type="checkbox"
                  aria-label={ctl.L("SELECT_PAGE")}
                  checked={allSelected}
                  data-mixed={anySelected ? "true" : "false"}
                  onChange={(event) => ctl.selectPage(event.target.checked)}
                />
              </th>
              <th
                data-column-id="Contact"
                data-column-label={ctl.field("Contact")}
                data-column-required="true"
                data-column-lead="38.4"
                aria-sort={direction("Contact") as "none"}
              >
                {header("Contact")}
              </th>
              {fields.map((field) => (
                <th
                  key={field}
                  className={columnClass(field)}
                  data-column-id={field}
                  data-column-label={ctl.field(field)}
                  aria-sort={direction(field) as "none"}
                  {...measure(field)}
                >
                  {header(field)}
                  {field === "Tags" && filterTags.length > 0 ? (
                    <span className="c2-tag-probe" aria-hidden="true">
                      <span className="c2-tag-probe-list">
                        {filterTags.map((tag) => (
                          <span key={tag} className="chip">
                            <ContactTagBadge name={tag} color={ctl.tagColor(tag)} />
                          </span>
                        ))}
                        <span className="button ghost c1-icon c2-tag-trigger">+99</span>
                      </span>
                    </span>
                  ) : null}
                </th>
              ))}
              {canChat ? (
                <th
                  className="c1-chat"
                  data-column-id="actions"
                  data-column-required="true"
                  data-column-label={ctl.L("CHAT")}
                >
                  <span className="sr-only">{ctl.L("CHAT")}</span>
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {page
              ? items.map((row) => {
                  const name = fullName(row.contact);
                  const thread = row.conversation;
                  return (
                    <tr
                      key={row.contact.id}
                      data-contact-row={row.contact.id}
                      aria-selected={selected.has(row.contact.id) ? "true" : "false"}
                      aria-current={open(row) as "true" | undefined}
                      onClick={() => void ctl.openContact(row.contact.id)}
                    >
                      <td className="c1-check" onClick={(event) => event.stopPropagation()}>
                        <input
                          type="checkbox"
                          aria-label={formatText(ctl.L("SELECT_CONTACT"), name)}
                          checked={selected.has(row.contact.id)}
                          onChange={(event) => ctl.select(row.contact.id, event.target.checked)}
                        />
                      </td>
                      <td className="c1-name">
                        <a
                          href={ctl.listUrl(row.contact.id)}
                          data-contact-open=""
                          onClick={(event) => {
                            event.stopPropagation();
                            ctl.openFromLink(event, row.contact.id);
                          }}
                        >
                          <ChatAvatar name={name} identity={row.contact.id} external className="c1-avatar" />
                          <span className="c2-cell-value" title={name}>
                            <Highlighted text={name} term={term} />
                          </span>
                        </a>
                      </td>
                      {fields.map((field) => (
                        <td key={field} className={columnClass(field)}>
                          {field === "Tags" ? (
                            <ContactTagList ctl={ctl} tags={row.contact.tags ?? []} name={name} filter={view.query.tags ?? []} />
                          ) : field === "Agent" && thread ? (
                            <span
                              className={`c1-agent ${thread.agentUserId === null && thread.agentEmployeeId === null ? "c1-unassigned" : ""}`}
                            >
                              {thread.agent !== null ? (
                                <ChatAvatar
                                  name={thread.agent}
                                  identity={thread.agentUserId ?? thread.agentEmployeeId ?? 0}
                                  agent={thread.agentEmployeeId !== null}
                                  className="c1-agent-avatar"
                                />
                              ) : null}
                              <span className="c2-cell-value" title={value(row, field)}>
                                {value(row, field)}
                              </span>
                            </span>
                          ) : (
                            <span className="c2-cell-value" title={value(row, field)}>
                              <Highlighted text={value(row, field)} term={term} />
                            </span>
                          )}
                        </td>
                      ))}
                      {canChat ? (
                        <td className="c1-chat" onClick={(event) => event.stopPropagation()}>
                          {opensThread(thread) ? (
                            <button
                              className="button ghost c1-icon icon-button"
                              aria-label={formatText(ctl.L("OPEN_CHAT"), name)}
                              title={ctl.L("CONVERSATIONS")}
                              onClick={(event) => {
                                event.stopPropagation();
                                void ctl.openSession(thread);
                              }}
                            >
                              <ChatIcon name="message-square-text" />
                            </button>
                          ) : null}
                        </td>
                      ) : null}
                    </tr>
                  );
                })
              : [0, 1, 2, 3, 4].map((row) => (
                  <tr key={row} className="e1-skeleton-row" aria-hidden="true">
                    <td colSpan={fields.length + 2 + (canChat ? 1 : 0)}>
                      <div className="e1-skeleton-line">
                        <span className="e1-skeleton-avatar"></span>
                        <span className="e1-skeleton-bar"></span>
                        <span className="e1-skeleton-bar e1-skeleton-short"></span>
                      </div>
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
      {directory ? (
        <div className="c2-directory" ref={listRoot}>
          <label className="checkbox-row c2-directory-select">
            <input type="checkbox" checked={allSelected} onChange={(event) => ctl.selectPage(event.target.checked)} />
            {ctl.L("SELECT_PAGE")}
          </label>
          {page
            ? items.map((row) => {
                const name = fullName(row.contact);
                const thread = row.conversation;
                return (
                  <article
                    key={row.contact.id}
                    className="c2-directory-row"
                    data-contact-row={row.contact.id}
                    aria-selected={selected.has(row.contact.id) ? "true" : "false"}
                    aria-current={open(row) as "true" | undefined}
                    onClick={() => void ctl.openContact(row.contact.id)}
                  >
                    <label
                      className={`c2-directory-check ${selected.size > 0 ? "c1-selection-active" : ""}`}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        aria-label={formatText(ctl.L("SELECT_CONTACT"), name)}
                        checked={selected.has(row.contact.id)}
                        onChange={(event) => ctl.select(row.contact.id, event.target.checked)}
                      />
                    </label>
                    <ChatAvatar name={name} identity={row.contact.id} external className="c1-mobile-avatar" />
                    <div className="c2-directory-body">
                      <a
                        className="c2-directory-name"
                        title={name}
                        href={ctl.listUrl(row.contact.id)}
                        data-contact-open=""
                        onClick={(event) => {
                          event.stopPropagation();
                          ctl.openFromLink(event, row.contact.id);
                        }}
                      >
                        <Highlighted text={name} term={term} />
                      </a>
                      <div className="c2-directory-phone">{phone(row.contact.phone)}</div>
                      <ContactTagList ctl={ctl} tags={row.contact.tags ?? []} name={name} filter={view.query.tags ?? []} />
                    </div>
                    {canChat && opensThread(thread) ? (
                      <button
                        className="button ghost c1-icon icon-button"
                        aria-label={formatText(ctl.L("OPEN_CHAT"), name)}
                        onClick={(event) => {
                          event.stopPropagation();
                          void ctl.openSession(thread);
                        }}
                      >
                        <ChatIcon name="message-square-text" />
                      </button>
                    ) : null}
                  </article>
                );
              })
            : [0, 1, 2, 3, 4].map((row) => (
                <div key={row} className="e1-skeleton-row e1-skeleton-card" aria-hidden="true">
                  <div className="e1-skeleton-line">
                    <span className="e1-skeleton-avatar"></span>
                    <span className="e1-skeleton-bar"></span>
                    <span className="e1-skeleton-bar e1-skeleton-short"></span>
                  </div>
                </div>
              ))}
        </div>
      ) : null}
    </>
  );
}

/** Paginado de escritorio (ContactPagination.razor): tamaño de página, rango y números con «…». */
export function ContactPagination({ ctl, page }: { ctl: ContactWorkspaceController; page: ContactPageResult }) {
  // C28 1.14.4 (J13): la misma regla que Etiquetas; sin paginado si todo entra en la opción más chica.
  const mode = pagerMode(page.total, page.pageSize);
  if (mode === "none") return null;
  const totalPages = Math.ceil(page.total / page.pageSize);
  const set = new Set<number>([1, totalPages]);
  for (let number = Math.max(1, page.page - 1); number <= Math.min(totalPages, page.page + 1); number++)
    set.add(number);
  const numbers: number[] = [];
  let previous = 0;
  for (const number of [...set].sort((a, b) => a - b)) {
    if (previous > 0 && number > previous + 1) numbers.push(0);
    numbers.push(number);
    previous = number;
  }
  return (
    <nav className="c1-pagination" aria-label={ctl.L("TITLE")}>
      <div className="c1-page-size">
        <span>{ctl.L("PAGE_SIZE")}</span>
        <ChoiceMenu
          label={ctl.L("PAGE_SIZE")}
          value={String(page.pageSize)}
          onChange={(value) => void ctl.size(Number(value))}
          options={[10, 25, 50, 100].map((size) => ({ value: String(size), label: String(size) }))}
          chevron={<ChatIcon name="chevron-down" />}
          check={<ChatIcon name="check" />}
          short
        />
      </div>
      {mode === "full" ? (
      <div className="actions">
        <span role="status" className="c1-range">
          {formatText(
            ctl.L2("RANGE"),
            count(page.total === 0 ? 0 : (page.page - 1) * page.pageSize + 1),
            count(Math.min(page.page * page.pageSize, page.total)),
            count(page.total),
          )}
        </span>
        <button
          className="button ghost c1-icon icon-button"
          aria-label={ctl.L("PREVIOUS")}
          disabled={page.page === 1}
          onClick={() => void ctl.pageTo(page.page - 1)}
          title={ctl.L("PREVIOUS")}
        >
          <ChatIcon name="chevron-left" />
        </button>
        <span className="c1-page-numbers">
          {numbers.map((number, index) =>
            number === 0 ? (
              <span key={"gap" + index} aria-hidden="true">
                …
              </span>
            ) : (
              <button
                key={number}
                className={`button ${page.page === number ? "secondary" : "ghost"} c1-icon`}
                aria-current={page.page === number ? "page" : undefined}
                onClick={() => void ctl.pageTo(number)}
              >
                {number}
              </button>
            ),
          )}
        </span>
        <button
          className="button ghost c1-icon icon-button"
          aria-label={ctl.L("NEXT")}
          disabled={page.page === totalPages}
          onClick={() => void ctl.pageTo(page.page + 1)}
          title={ctl.L("NEXT")}
        >
          <ChatIcon name="chevron-right" />
        </button>
      </div>
      ) : null}
    </nav>
  );
}
